import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SEASONS, CATEGORIES, FREE_SEASON } from "@/data/categories";
import { MemoryList, sortMemories } from "@/components/MemoryList";
import { SeasonNotebook } from "@/components/SeasonNotebook";
import { SeasonCover } from "@/components/SeasonCover";
import { AddMemoryWizard } from "@/components/AddMemoryWizard";
import { EventDetailDialog } from "@/components/EventDetailDialog";
import { PromptDialog } from "@/components/PromptDialog";
import { InsightsView } from "@/components/InsightsView";
import { KeepsakesView } from "@/components/KeepsakesView";
import { PremiumStrip } from "@/components/PremiumStrip";
import { LifetimeBanner } from "@/components/LifetimeBanner";
import { RecapDialog } from "@/components/RecapDialog";
import { ComplimentaryInsightDialog } from "@/components/ComplimentaryInsightDialog";
import { TrialBanner } from "@/components/TrialBanner";
import { PausedBanner } from "@/components/PausedBanner";
import { AccountDataDialog } from "@/components/AccountDataDialog";
import { UnlockCelebration } from "@/components/UnlockCelebration";
import { InstallAppCard } from "@/components/InstallAppCard";
import { AccessNotice } from "@/components/AccessNotice";
import {
  BookOpen, Plus, SignOut, Clock, UserCircle, SquaresFour, Tree, Star, Lock, Sparkle, Compass, Gift, Receipt,
} from "@phosphor-icons/react";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({ total: 0, dreams: 0, by_category: {}, by_season: {} });
  const [trial, setTrial] = useState({ is_pro: false, days_left: null });
  const [view, setView] = useState(() => {
    const v = new URLSearchParams(window.location.search).get("view");
    return ["seasons", "timeline", "categories", "insights", "keepsakes", "dreams"].includes(v) ? v : "seasons";
  });
  const [wizardType, setWizardType] = useState(null); // "memory" | "dream" | null
  const [wizardSeason, setWizardSeason] = useState(null); // pre-selected season (skips the season step)
  const [selected, setSelected] = useState(null);
  const [promptMemory, setPromptMemory] = useState(null);
  const [recapOpen, setRecapOpen] = useState(false);
  const [compOpen, setCompOpen] = useState(false);
  const [comp, setComp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dataOpen, setDataOpen] = useState(false);
  const [openSeason, setOpenSeason] = useState(null);
  const [focusInsight, setFocusInsight] = useState(false);

  const [celebrate, setCelebrate] = useState(false);

  const isPro = trial.is_pro;

  useEffect(() => {
    if (!isPro || !new URLSearchParams(window.location.search).has("celebrate")) return;
    setCelebrate(true);
    const url = new URL(window.location.href);
    url.searchParams.delete("celebrate");
    window.history.replaceState(null, "", url.pathname + url.search);
  }, [isPro]);

  const load = useCallback(async () => {
    try {
      const [e, s, t] = await Promise.all([
        api.get("/events"),
        api.get("/events/stats"),
        api.get("/trial"),
      ]);
      setEvents(e.data);
      setStats(s.data);
      setTrial(t.data);
    } catch (err) {
      if (err.response?.status === 401) {
        await logout();
        navigate("/auth");
        return;
      }
      toast.error("We couldn't load your journey. Please refresh.");
    } finally {
      setLoading(false);
    }
  }, [logout, navigate]);

  const loadComplimentary = useCallback(async () => {
    try {
      const r = await api.get("/insights/complimentary");
      setComp(r.data);
    } catch (e) { /* non-blocking */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Deep link from checkout / payment result: /timeline?memory=<id>&insight=1
  useEffect(() => {
    if (loading) return;
    const q = new URLSearchParams(window.location.search);
    const id = q.get("memory");
    const ev = id && events.find((e) => e.id === id);
    if (ev) {
      setFocusInsight(q.get("insight") === "1");
      setSelected(ev);
      window.history.replaceState(null, "", "/timeline");
    }
  }, [loading, events]);

  const insightAccess = (ev) => {
    if (user?.role === "admin") return "admin";
    if (ev.insight_unlocked) return "paid";
    if (comp?.claimed) return comp.event_id === ev.id ? "gift" : null;
    if (comp?.eligible && ev.season === FREE_SEASON) return "gift";
    return null;
  };

  const chooseComplimentary = () => {
    setCompOpen(false);
    const ev = comp?.claimed && events.find((e) => e.id === comp.event_id);
    if (ev) { setFocusInsight(true); setSelected(ev); return; }
    setView("seasons");
    setOpenSeason(FREE_SEASON);
  };
  useEffect(() => { loadComplimentary(); }, [loadComplimentary]);

  // The conversion moment: once a trial user has recorded enough memories, gently
  // surface their complimentary Personal Insight (shown once per session). Skip it
  // when the user deep-linked to a specific tab (e.g. ?view=keepsakes) so the
  // dialog never blocks the CTA they came for.
  useEffect(() => {
    const deepLinked = new URLSearchParams(window.location.search).has("view");
    if (!loading && comp?.eligible && !recapOpen && !promptMemory && !deepLinked) {
      if (sessionStorage.getItem("comp-insight-seen") !== "1") {
        setCompOpen(true);
        sessionStorage.setItem("comp-insight-seen", "1");
      }
    }
  }, [loading, comp, recapOpen, promptMemory]);

  useEffect(() => {
    if (!loading && !isPro && trial.days_left != null && trial.days_left <= 1) {
      if (sessionStorage.getItem("recap-seen") !== "1") {
        setRecapOpen(true);
        sessionStorage.setItem("recap-seen", "1");
      }
    }
  }, [loading, isPro, trial.days_left]);

  const doLogout = () => { logout(); navigate("/"); };
  const goUpgrade = () => navigate("/upgrade");

  // Open the full-page Add Memory experience. If a season is given (or a free
  // user who only has Childhood), it's carried forward via the query string.
  const startMemory = (season = null) => {
    if (trial.paused) { toast.error("Your diary is paused. Upgrade to add new memories."); return; }
    const s = season || (!isPro ? FREE_SEASON : null);
    navigate(s ? `/add-memory?season=${encodeURIComponent(s)}` : "/add-memory");
  };

  const handleCreated = (created) => {
    load();
    loadComplimentary();
    if (created && created.entry_type !== "dream") setPromptMemory(created);
  };

  const memories = events.filter((e) => e.entry_type !== "dream");
  const dreams = events.filter((e) => e.entry_type === "dream");

  const startDream = () => {
    if (!isPro) return goUpgrade();
    setWizardSeason(null);
    setWizardType("dream");
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-16 z-20 border-b border-[#4A4540]/10 bg-[#FDFBF7]/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-2 px-4 py-3 sm:px-6 sm:py-4">
          <div className="flex min-w-0 items-center gap-2">
            <BookOpen size={24} weight="duotone" className="shrink-0 text-[#B08D57]" />
            <span className="truncate font-serif text-base text-[#2C2A28] sm:text-lg"><span className="sm:hidden">My Life Journey</span><span className="hidden sm:inline">Your Life Journeys Matter</span></span>
          </div>
          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
            {user?.role === "admin" && (
              <Button data-testid="header-payment-history-btn" onClick={() => navigate("/admin/payments")} variant="outline"
                className="rounded-full border-[#B08D57] px-3 text-[#8A5A2B] hover:bg-[#F4EFE6]">
                <Receipt size={18} weight="duotone" /><span className="hidden sm:inline">Payments</span>
              </Button>
            )}
            {!isPro && (
              <Button
                data-testid="header-upgrade-btn"
                onClick={goUpgrade}
                variant="outline"
                className="hidden rounded-full border-[#B08D57] text-[#B08D57] hover:bg-[#B08D57] hover:text-white sm:flex"
              >
                Lifetime Access
              </Button>
            )}
            <Button
              data-testid="open-add-memory-btn"
              onClick={() => startMemory()}
              className="rounded-full bg-[#B08D57] px-3 text-[#FDFBF7] transition-transform hover:-translate-y-0.5 hover:bg-[#C19A6B] sm:px-4"
            >
              <Plus size={18} weight="bold" className="sm:mr-1" /> <span className="hidden min-[400px]:inline">Add memory</span><span className="min-[400px]:hidden">Add</span>
            </Button>
            <Button
              data-testid="account-data-btn"
              onClick={() => setDataOpen(true)}
              variant="ghost"
              size="icon"
              title="Your data: download or delete"
              className="rounded-full text-[#8A7F76] hover:bg-[#F4EFE6]"
            >
              <UserCircle size={22} weight="duotone" />
            </Button>
            <Button
              data-testid="logout-btn"
              onClick={doLogout}
              variant="ghost"
              size="icon"
              className="rounded-full text-[#8A7F76] hover:bg-[#F4EFE6]"
            >
              <SignOut size={20} weight="duotone" />
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8">
        {trial.paused ? (
          <div className="mb-8">
            <PausedBanner deleteAt={trial.delete_at} onUpgrade={goUpgrade} onManage={() => setDataOpen(true)} />
          </div>
        ) : trial.read_only ? (
          <div className="mb-8">
            <AccessNotice notice={trial.access_notice} onUpgrade={goUpgrade} />
          </div>
        ) : !isPro && trial.days_left != null && (
          <div className="mb-8">
            <TrialBanner daysLeft={trial.days_left} onUpgrade={goUpgrade} />
          </div>
        )}
        <AccountDataDialog open={dataOpen} onOpenChange={setDataOpen} />
        <UnlockCelebration open={celebrate} onOpenChange={setCelebrate} />
        <InstallAppCard />

        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-handwriting text-3xl text-[#B08D57]">
              hello{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
            </p>
            <h1 className="font-serif text-4xl tracking-tight text-[#2C2A28]">Your life so far</h1>
          </div>
          <Button
            data-testid="add-dream-btn"
            onClick={startDream}
            variant="outline"
            className="rounded-full border-[#C99A5B] text-[#8A5A2B] hover:bg-[#DDA77B]/20"
          >
            {isPro ? <Star size={16} weight="duotone" className="mr-1" /> : <Lock size={14} weight="fill" className="mr-1" />}
            Add a dream
          </Button>
        </div>


        {!(view === "seasons" && openSeason) && <div data-testid="dashboard-stats" className="mb-8 grid grid-cols-2 gap-3 sm:mb-10 sm:grid-cols-4 sm:gap-4">
          <StatCard label="Memories kept" value={stats.total} />
          <StatCard label="Dreams" value={stats.dreams} />
          <StatCard label="Seasons touched" value={Object.keys(stats.by_season).length} />
          <StatCard label="Categories" value={Object.keys(stats.by_category).length} />
        </div>}

        <div className="mb-8">
          <Tabs value={view} onValueChange={setView}>
            <TabsList className="no-scrollbar flex h-auto w-full justify-start overflow-x-auto bg-[#F4EFE6] sm:w-auto">
              <TabsTrigger data-testid="tab-seasons" value="seasons" className="min-h-[40px] shrink-0 px-3 data-[state=active]:bg-white">
                <Tree size={16} weight="duotone" className="mr-1" /> Seasons
              </TabsTrigger>
              <TabsTrigger data-testid="tab-timeline" value="timeline" className="min-h-[40px] shrink-0 px-3 data-[state=active]:bg-white">
                <Clock size={16} weight="duotone" className="mr-1" /> Timeline
              </TabsTrigger>
              <TabsTrigger data-testid="tab-categories" value="categories" className="min-h-[40px] shrink-0 px-3 data-[state=active]:bg-white">
                <SquaresFour size={16} weight="duotone" className="mr-1" /> Categories
              </TabsTrigger>
              <TabsTrigger data-testid="tab-insights" value="insights" className="min-h-[40px] shrink-0 px-3 data-[state=active]:bg-white">
                <Compass size={16} weight="duotone" className="mr-1" /> Insights
              </TabsTrigger>
              <TabsTrigger data-testid="tab-keepsakes" value="keepsakes" className="min-h-[40px] shrink-0 px-3 data-[state=active]:bg-white">
                <Gift size={16} weight="duotone" className="mr-1" /> Keepsakes
              </TabsTrigger>
              <TabsTrigger data-testid="tab-dreams" value="dreams" className="min-h-[40px] shrink-0 px-3 data-[state=active]:bg-white">
                <Star size={16} weight="duotone" className="mr-1" /> Dreams
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {loading ? (
          <p className="text-[#8A7F76]">Gathering your memories…</p>
        ) : view === "seasons" ? (
          <SeasonsView openId={openSeason} setOpenId={setOpenSeason} memories={memories} isPro={isPro} onSelect={(ev) => { setFocusInsight(false); setSelected(ev); }} onUpgrade={goUpgrade} onAddToSeason={startMemory} />
        ) : view === "timeline" ? (
          <TimelineView memories={memories} onSelect={setSelected} onAdd={() => startMemory()} />
        ) : view === "categories" ? (
          <CategoryView memories={memories} onSelect={setSelected} />
        ) : view === "insights" ? (
          <InsightsView
            isPro={isPro}
            onUpgrade={goUpgrade}
            comp={comp}
            onOpenComplimentary={() => setCompOpen(true)}
          />
        ) : view === "keepsakes" ? (
          <KeepsakesView isPro={isPro} />
        ) : (
          <DreamsView dreams={dreams} isPro={isPro} onSelect={setSelected} onAdd={startDream} onUpgrade={goUpgrade} />
        )}
        {!loading && view === "seasons" && !openSeason && (!isPro || stats.total > 0) && (
          <div className="mt-12">
            {!isPro && <LifetimeBanner />}
            {stats.total > 0 && <PremiumStrip onOpen={() => { setView("keepsakes"); document.querySelector('[data-testid="tab-keepsakes"]')?.scrollIntoView({ behavior: "smooth", block: "start" }); }} />}
          </div>
        )}
      </main>

      <AddMemoryWizard
        open={!!wizardType}
        entryType={wizardType || "memory"}
        isPro={isPro}
        lockedSeason={wizardSeason}
        onOpenChange={(o) => { if (!o) { setWizardType(null); setWizardSeason(null); } }}
        onCreated={handleCreated}
      />
      <PromptDialog
        memory={promptMemory}
        open={!!promptMemory}
        onOpenChange={(o) => !o && setPromptMemory(null)}
        onSaved={load}
      />
      <RecapDialog
        open={recapOpen}
        onOpenChange={setRecapOpen}
        memories={memories}
        stats={stats}
        daysLeft={trial.days_left ?? 0}
      />
      <ComplimentaryInsightDialog
        open={compOpen}
        onOpenChange={setCompOpen}
        comp={comp}
        onChoose={chooseComplimentary}
      />
      <EventDetailDialog
        event={selected}
        open={!!selected}
        onOpenChange={(o) => { if (!o) { setSelected(null); setFocusInsight(false); } }}
        onChanged={() => { load(); loadComplimentary(); }}
        insightAccess={insightAccess}
        focusInsight={focusInsight}
      />
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="rounded-lg border border-[#4A4540]/10 bg-white p-4 shadow-sm sm:p-5">
      <p className="font-serif text-3xl sm:text-4xl text-[#2C2A28]">{value}</p>
      <p className="mt-1 text-xs uppercase tracking-wide text-[#8A7F76]">{label}</p>
    </div>
  );
}

function SeasonsView({ openId, setOpenId, memories, isPro, onSelect, onUpgrade, onAddToSeason }) {
  const open = SEASONS.find((x) => x.id === openId);
  if (open) {
    return (
      <SeasonNotebook season={open} items={memories.filter((m) => m.season === open.id)}
        onBack={() => setOpenId(null)} onSelect={onSelect}
        onAdd={!isPro && open.id !== FREE_SEASON ? onUpgrade : onAddToSeason} />
    );
  }
  return (
    <div data-testid="season-covers" className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {SEASONS.map((season, i) => (
        <SeasonCover key={season.id} season={season} index={i} items={memories.filter((m) => m.season === season.id)}
          isPro={isPro} locked={!isPro && season.id !== FREE_SEASON} onOpen={() => setOpenId(season.id)} onUpgrade={onUpgrade} />
      ))}
    </div>
  );
}

function TimelineView({ memories, onSelect, onAdd }) {
  if (memories.length === 0) return <EmptyState onAdd={onAdd} />;
  const groups = SEASONS.map((s) => ({ s, items: sortMemories(memories.filter((m) => m.season === s.id)) })).filter((g) => g.items.length);
  return (
    <div className="space-y-8" data-testid="timeline-list">
      {groups.map(({ s, items }) => (
        <section key={s.id}>
          <h2 className="mb-3 font-serif text-xl text-[#2C2A28]">{s.label} <span className="text-sm text-[#8A7F76]">({items.length})</span></h2>
          <MemoryList items={items} onSelect={onSelect} testId={`timeline-season-${s.id}`} />
        </section>
      ))}
    </div>
  );
}

function CategoryView({ memories, onSelect }) {
  const present = CATEGORIES.filter((c) => memories.some((e) => e.category === c.id));
  if (present.length === 0) return <p className="text-[#8A7F76]">No categorised memories yet.</p>;
  return (
    <div className="space-y-8">
      {present.map((cat) => {
        const items = sortMemories(memories.filter((e) => e.category === cat.id));
        return (
          <section key={cat.id} data-testid={`category-section-${cat.id}`}>
            <div className="mb-3 flex items-center gap-3">
              <span className="h-3 w-3 rounded-full" style={{ backgroundColor: cat.color }} />
              <h2 className="font-serif text-xl text-[#2C2A28]">{cat.label}</h2>
              <span className="text-sm text-[#8A7F76]">({items.length})</span>
            </div>
            <MemoryList items={items} onSelect={onSelect} showSeason testId={`category-list-${cat.id}`} />
          </section>
        );
      })}
    </div>
  );
}

function DreamsView({ dreams, isPro, onSelect, onAdd, onUpgrade }) {
  if (!isPro) {
    return (
      <div className="flex flex-col items-center py-16 text-center">
        <Sparkle size={40} weight="duotone" className="text-[#C99A5B]" />
        <h3 className="mt-4 font-serif text-2xl text-[#2C2A28]">Dream forward, not just back</h3>
        <p className="mt-2 max-w-sm text-[#8A7F76]">
          Write your dreams, goals, and letters to your future self. Available
          with Lifetime Access.
        </p>
        <Button
          data-testid="dreams-upgrade-btn"
          onClick={onUpgrade}
          className="mt-6 rounded-full bg-[#B08D57] text-[#FDFBF7] hover:bg-[#C19A6B]"
        >
          Unlock with Lifetime Access
        </Button>
      </div>
    );
  }
  if (dreams.length === 0) {
    return (
      <div className="flex flex-col items-center py-16 text-center">
        <Star size={40} weight="duotone" className="text-[#C99A5B]" />
        <h3 className="mt-4 font-serif text-2xl text-[#2C2A28]">No dreams yet</h3>
        <p className="mt-2 max-w-sm text-[#8A7F76]">Add a hope, goal, or letter to your future self.</p>
        <Button data-testid="dreams-add-btn" onClick={onAdd} className="mt-6 rounded-full bg-[#C99A5B] text-white hover:bg-[#d0a561]">
          <Star size={16} weight="duotone" className="mr-1" /> Add a dream
        </Button>
      </div>
    );
  }
  return (
    <MemoryList items={sortMemories(dreams, "newest")} onSelect={onSelect} testId="dreams-list" />
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="flex flex-col items-center py-20 text-center">
      <div className="w-52 -rotate-3 bg-white p-3 pb-8 shadow-xl">
        <div className="flex aspect-[4/3] w-full items-center justify-center bg-[#DDA77B]/25">
          <BookOpen size={48} weight="duotone" className="text-[#B08D57]" />
        </div>
        <p className="mt-3 font-handwriting text-2xl text-[#4A4540]">start here</p>
      </div>
      <h3 className="mt-8 font-serif text-2xl text-[#2C2A28]">Your journey is empty</h3>
      <p className="mt-1 max-w-sm text-[#8A7F76]">
        Add your first memory — a moment from childhood, a lesson learned, or
        something that shaped who you are.
      </p>
      <Button data-testid="empty-add-btn" onClick={onAdd} className="mt-6 rounded-full bg-[#B08D57] text-[#FDFBF7] hover:bg-[#C19A6B]">
        <Plus size={18} weight="bold" className="mr-1" /> Add your first memory
      </Button>
    </div>
  );
}
